"use client";

import React from "react";
import { ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheTrustedInspectionsAdvantage() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-trusted-inspections-advantage" className="relative bg-background py-24 lg:py-32 overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Left Column - Image with Overlay Badge */}
          <div className="relative w-full h-full min-h-[400px] lg:min-h-[600px] rounded-xl overflow-hidden shadow-2xl group">
            {/* Subtle Gradient Overlay */}
            <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-500 z-10" />
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/image_kh4skyte.png" alt="German Llosa - Lead Inspector at Trusted Inspections" className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105" />
            {/* Bottom Gradient for Text Readability */}
            <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/60 to-transparent z-10" />
            <div className="absolute bottom-6 left-6 z-20 flex items-center gap-3">
              <div className="bg-primary text-primary-foreground p-2 rounded-full">
                <CircleCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="text-white font-bold text-lg leading-tight">German Llosa</p>
                <p className="text-white/80 text-sm font-medium">Owner & Lead Inspector</p>
              </div>
            </div>
          </div>

          {/* Right Column - Text Content */}
          <div className="relative flex flex-col justify-center h-full lg:py-12">
            {/* Subtle Watermark/Pattern in background */}
            <div className="absolute inset-0 -z-10 opacity-[0.03] pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/topographic_watermark.png)] bg-cover bg-center" />
            
            <div className="relative z-10">
              <span className="inline-block text-primary font-bold tracking-widest uppercase text-sm mb-4">
                Who We Are
              </span>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground mb-6 leading-tight">
                The Trusted Inspections <span className="text-primary">Advantage</span>
              </h2>
              
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed mb-10">
                <p>
                  Trusted Inspections is a family-owned South Florida home inspection company led by German Llosa, a licensed and insured inspector with 5+ years of local experience. We specialize in helping home buyers, sellers, and insurance clients understand property condition with clarity and confidence. Our photo-rich reports arrive within 24 hours—often same day. Whether you're closing on a home, protecting your investment, or meeting insurance requirements, our team delivers the clear, honest inspections South Florida families trust.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="text-base h-14 px-8 shadow-lg shadow-primary/20">
                  <Link to="#schedule-your-inspection">
                    Schedule an Inspection
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-base h-14 px-8 border-2 hover:bg-primary/5">
                  <Link to="#our-inspection-services">
                    View Services
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}